import { useEffect, useMemo } from "react";
import { isDialogOpen } from "../state/dialogStore";
import type { BinaryLabel } from "../types";
import { directionButtonClass } from "./directionButton";

/**
 * The Simple-mode guess controls (spec §7, §9): Higher / Lower on ↑ / ↓ (and
 * U / D) by default. A binary pair (text + lowercase letter key, per up/down)
 * can be supplied to relabel the same up/down mechanics: Predict weekly's
 * footer passes Green/Red on g/r instead. Fires the chosen label; holds no
 * game state.
 */

type Choice = { label: BinaryLabel; text: string; key: string };

export type BinaryChoicePair = {
  up: { text: string; letterKey: string };
  down: { text: string; letterKey: string };
};

const DEFAULT_BINARY_PAIR: BinaryChoicePair = {
  up: { text: "Higher", letterKey: "u" },
  down: { text: "Lower", letterKey: "d" },
};

export type GuessControlsProps = {
  onGuess: (label: BinaryLabel) => void;
  disabled?: boolean;
  binaryPair?: BinaryChoicePair;
};

export function GuessControls({
  onGuess,
  disabled,
  binaryPair = DEFAULT_BINARY_PAIR,
}: GuessControlsProps) {
  const choices = useMemo<Choice[]>(
    () => [
      { label: "up", text: binaryPair.up.text, key: "↑" },
      { label: "down", text: binaryPair.down.text, key: "↓" },
    ],
    [binaryPair],
  );

  const keyToLabel = useMemo<Record<string, BinaryLabel>>(
    () => ({
      ArrowUp: "up",
      ArrowDown: "down",
      [binaryPair.up.letterKey]: "up",
      [binaryPair.down.letterKey]: "down",
    }),
    [binaryPair],
  );

  useEffect(() => {
    if (disabled) return;
    const valid = new Set(choices.map((c) => c.label));
    const handler = (event: KeyboardEvent) => {
      // No guess key fires while a dialog (onboarding, data/simulation,
      // feedback) is open (GRA-146).
      if (isDialogOpen()) return;
      // Ignore keystrokes aimed at a field (e.g. the reason note, GRA-135):
      // matches TradePanel's own guard on its window-level shortcuts.
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const label = keyToLabel[event.key] ?? keyToLabel[event.key.toLowerCase()];
      // Ignore keys that don't map to a choice (e.g. → / S).
      if (label && valid.has(label)) {
        event.preventDefault();
        onGuess(label);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onGuess, disabled, choices, keyToLabel]);

  return (
    <div className="flex flex-wrap items-center justify-center gap-3">
      {choices.map((choice) => (
        <button
          key={choice.label}
          type="button"
          disabled={disabled}
          onClick={() => onGuess(choice.label)}
          className={directionButtonClass(choice.label)}
        >
          <span aria-hidden="true" className="opacity-70">
            {choice.key}
          </span>
          {choice.text}
        </button>
      ))}
    </div>
  );
}
